/**
 * Per-instance pointer-event-based drag-to-reorder for a vertical
 * list. Modeled on RouteView's drag, generalized so the manage
 * modals (groups, cities, and future lists) share one implementation.
 *
 *   const drag = createListDrag({
 *     onCommit: (fromIdx, toIdx) => { ... },
 *   });
 *
 *   {#each items as item, idx}
 *     <li
 *       bind:this={drag.setRowEl(idx)}
 *       onpointerdown={(e) => drag.onDown(e, idx)}
 *       onpointermove={drag.onMove}
 *       onpointerup={drag.onUp}
 *       onpointercancel={drag.onCancel}
 *       style={drag.rowStyle(idx)}
 *     >…</li>
 *   {/each}
 *
 * onCommit is called exactly once per real drag (when the target
 * slot differs from the source). The caller does the actual list
 * reorder + persistence; this helper only computes "user dragged
 * from i → j."
 */
export function createListDrag(opts: {
  onCommit: (fromIdx: number, toIdx: number) => void;
  /** If a pointerdown originates inside an element matching this
   *  selector, drag is suppressed — lets edit/delete buttons inside
   *  a row keep their click. */
  ignoreSelector?: string;
}) {
  let drag = $state<{
    fromIdx: number;
    startY: number;
    currentY: number;
    targetIdx: number;
    pointerId: number;
  } | null>(null);

  let rowEls: (HTMLElement | null)[] = $state([]);

  function getRowEl(idx: number): HTMLElement | null {
    return rowEls[idx] ?? null;
  }

  function setRowEl(idx: number, el: HTMLElement | null) {
    rowEls[idx] = el;
  }

  function computeTargetIdx(clientY: number, count: number): number {
    for (let i = 0; i < rowEls.length; i++) {
      const el = rowEls[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return count;
  }

  function onDown(e: PointerEvent, idx: number) {
    if (opts.ignoreSelector) {
      const target = e.target as HTMLElement;
      if (target.closest(opts.ignoreSelector)) return;
    }
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag = {
      fromIdx: idx,
      startY: e.clientY,
      currentY: e.clientY,
      targetIdx: idx,
      pointerId: e.pointerId,
    };
  }

  function onMove(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.pointerId) return;
    drag = {
      ...drag,
      currentY: e.clientY,
      targetIdx: computeTargetIdx(e.clientY, rowEls.length),
    };
  }

  function onUp(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const { fromIdx, targetIdx } = drag;
    drag = null;
    // Two no-op cases: dropping back into the same slot, or into the
    // gap immediately after the source (which is also the same slot
    // once you account for the removal+insert shift).
    if (targetIdx === fromIdx || targetIdx === fromIdx + 1) return;
    // Adjust the target index for the fact that removing the source
    // shifts everything after it down by 1.
    const insertAt = targetIdx > fromIdx ? targetIdx - 1 : targetIdx;
    opts.onCommit(fromIdx, insertAt);
  }

  function onCancel() {
    drag = null;
  }

  function rowStyle(idx: number): string {
    if (!drag || drag.fromIdx !== idx) return '';
    const dy = drag.currentY - drag.startY;
    return `transform: translateY(${dy}px); z-index: 5;`;
  }

  function isDraggingRow(idx: number): boolean {
    return drag !== null && drag.fromIdx === idx;
  }

  return {
    getRowEl,
    setRowEl,
    onDown,
    onMove,
    onUp,
    onCancel,
    rowStyle,
    isDraggingRow,
    get rowCount() {
      return rowEls.length;
    },
    syncRowCount(n: number) {
      if (rowEls.length > n) rowEls.length = n;
    },
  };
}
